import { createSlice } from '@reduxjs/toolkit'
import { initialRoomState } from '@/redux/initialState/room'
import {
  createRoom,
  deleteRoom,
  fetchDashboardKPIs,
  fetchRooms,
  fetchCharterClasses,
  getRoomDetails,
  resendInvite,
  sendInvite,
  removePlayer,
  updateRoomDetails,
  getRoomMembers,
  duplicateRoom,
  leaveProfessorGame,
} from '@/redux/asyncThunks/room'
import { GameroomEvent } from '@/redux/types/room'

const roomSlice = createSlice({
  name: 'room',
  initialState: initialRoomState,
  reducers: {
    clearRoom: (state) => {
      state.currentRoom = null
      state.error = null
    },
    clearError: (state) => {
      state.error = null
    },
    clearInviteError: (state) => {
      state.inviteStatus.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createRoom.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createRoom.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentRoom = action.payload
        // Keep the two lists separate — a new Charter Class must not appear on
        // the game dashboard (and vice versa).
        if (action.payload?.is_charter_only) {
          state.charterClasses = [...state.charterClasses, action.payload]
        } else {
          state.rooms = [...state.rooms, action.payload]
        }
        state.error = null
      })
      .addCase(createRoom.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(duplicateRoom.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(duplicateRoom.fulfilled, (state, action) => {
        state.isLoading = false
        state.rooms = [...state.rooms, action.payload]
        state.error = null
      })
      .addCase(duplicateRoom.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(fetchRooms.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchRooms.fulfilled, (state, action) => {
        state.isLoading = false
        state.rooms = action.payload
        state.error = null
      })
      .addCase(fetchRooms.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(fetchCharterClasses.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCharterClasses.fulfilled, (state, action) => {
        state.isLoading = false
        state.charterClasses = action.payload
        state.error = null
      })
      .addCase(fetchCharterClasses.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(fetchDashboardKPIs.fulfilled, (state, action) => {
        state.dashboardKPIs = action.payload
      })
      .addCase(fetchDashboardKPIs.rejected, (state, action) => {
        state.error = action.payload as string
      })
      .addCase(deleteRoom.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteRoom.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(deleteRoom.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(getRoomDetails.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getRoomDetails.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentRoom = action.payload
        state.error = null
      })
      .addCase(getRoomDetails.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(sendInvite.pending, (state) => {
        state.inviteStatus.isLoading = true
        state.inviteStatus.error = null
        state.inviteStatus.success = false
      })
      .addCase(sendInvite.fulfilled, (state) => {
        state.inviteStatus.isLoading = false
        state.inviteStatus.error = null
        state.inviteStatus.success = true
      })
      .addCase(sendInvite.rejected, (state, action) => {
        state.inviteStatus.isLoading = false
        state.inviteStatus.error = action.payload as string
        state.inviteStatus.success = false
      })
      .addCase(resendInvite.pending, (state) => {
        state.inviteStatus.isLoading = true
        state.inviteStatus.error = null
        state.inviteStatus.success = false
      })
      .addCase(resendInvite.fulfilled, (state) => {
        state.inviteStatus.isLoading = false
        state.inviteStatus.error = null
        state.inviteStatus.success = true
      })
      .addCase(resendInvite.rejected, (state, action) => {
        state.inviteStatus.isLoading = false
        state.inviteStatus.error = action.payload as string
        state.inviteStatus.success = false
      })
      .addCase(getRoomMembers.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getRoomMembers.fulfilled, (state, action) => {
        state.isLoading = false
        state.roomMembers = action.payload
        state.error = null
      })
      .addCase(getRoomMembers.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(updateRoomDetails.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateRoomDetails.fulfilled, (state, action) => {
        state.isLoading = false
        // Update the current room's events with the new data
        state.currentRoom!.events = action.payload.events as GameroomEvent[]
        state.currentRoom!.scenario_title = action.payload
          .scenario_title as string
        state.currentRoom!.status = action.payload.status
        state.currentRoom!.gameroom_llm = action.payload.gameroom_llm
        if (action.payload.auto_approve !== undefined) {
          state.currentRoom!.auto_approve = action.payload.auto_approve
        }
        if (action.payload.enable_chat_history_summarization !== undefined) {
          state.currentRoom!.enable_chat_history_summarization =
            action.payload.enable_chat_history_summarization
        }
        if (action.payload.resource_limits_enabled !== undefined) {
          state.currentRoom!.resource_limits_enabled =
            action.payload.resource_limits_enabled
        }
        if (action.payload.resource_enforcement_mode !== undefined) {
          state.currentRoom!.resource_enforcement_mode =
            action.payload.resource_enforcement_mode
        }
        if (action.payload.default_currency_amount !== undefined) {
          state.currentRoom!.default_currency_amount =
            action.payload.default_currency_amount
        }
        if (action.payload.default_message_limit !== undefined) {
          state.currentRoom!.default_message_limit =
            action.payload.default_message_limit
        }
        if (action.payload.default_token_limit !== undefined) {
          state.currentRoom!.default_token_limit =
            action.payload.default_token_limit
        }
        if (action.payload.default_message_limit !== undefined) {
          state.currentRoom!.default_message_limit =
            action.payload.default_message_limit
        }
        if (action.payload.openai_api_key_configured !== undefined) {
          state.currentRoom!.openai_api_key_configured =
            action.payload.openai_api_key_configured
        }
        if (action.payload.claude_api_key_configured !== undefined) {
          state.currentRoom!.claude_api_key_configured =
            action.payload.claude_api_key_configured
        }
        state.error = null
      })
      .addCase(updateRoomDetails.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(removePlayer.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(removePlayer.fulfilled, (state, action) => {
        state.isLoading = false
        state.roomMembers = state.roomMembers.filter(
          (player) => player.gamealias !== action.meta.arg.playerId
        )
        state.error = null
      })
      .addCase(removePlayer.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(leaveProfessorGame.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(leaveProfessorGame.fulfilled, (state, action) => {
        state.isLoading = false
        state.rooms = state.rooms.filter((r) => r.roomid !== action.payload)
        state.error = null
      })
      .addCase(leaveProfessorGame.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const { clearRoom, clearError, clearInviteError } = roomSlice.actions
export default roomSlice.reducer
